// FIXME: remove eventually
import BrazilStates from '@/data/br-States.json';
import {
  beneficiaryTypes,
  customerIndustryTypes,
  entityTypes,
  industryTypes,
  occupationTypes,
  sourceOfWealthTypes,
} from '@/data/labels';
import { makeParagraph } from '@/locales/template';
import {
  BeneficiaryType,
  Customer,
  CustomerCompanyDetailsPatchRequest,
  EntityType,
} from '@/types/customer';
import { Field, FieldType, ItemsResolver, javaEnumToSelectItem, SelectItem } from '@/types/fields';
import * as kyb from '@/types/kyb';
import { ChildStep, ParentStep } from '@/types/steps';

const hasBusinessCustomers = (data: Customer): boolean => {
  const { businessInfo } = data;
  if (!businessInfo) {
    return false;
  }

  const { operations } = businessInfo;
  if (!operations) {
    return false;
  }

  const { customers } = operations;
  if (!customers) {
    return false;
  }

  const { customerType } = customers ?? { customerType: [] };
  return (
    customerType?.some((type: BeneficiaryType): boolean => type === BeneficiaryType.business) ??
    false
  );
};

const getSoleProprietorSkipMessage = (customer: Customer): string | undefined => {
  const { businessInfo } = customer ?? {};
  const { entityType } = businessInfo ?? { entityType: null };

  switch (entityType) {
    case EntityType.empresaIndividualEI:
    case EntityType.microEmpreendedorIndividualMEI:
      return 'VERIFY_BUSINESS_SOLE_PROPRIETOR_SKIP';
    default:
      return undefined;
  }
};

const createAddressFields = (statesResolver?: ItemsResolver<string>): readonly Field<any>[] => [
  {
    name: 'streetAddress',
    label: 'VERIFY_BUSINESS_ADDRESS',
    type: FieldType.text,
    columnSpan: 3,
    required: true,
  },
  {
    name: 'complement',
    label: 'VERIFY_BUSINESS_SUITE_FLOOR_APT',
    type: FieldType.text,
    columnSpan: 3,
    required: false,
  },
  {
    name: 'state',
    label: 'VERIFY_BUSINESS_STATE',
    type: FieldType.select,
    columnSpan: 1,
    source:
      statesResolver ??
      BrazilStates.map(
        (state: { readonly sigla: string; readonly nome: string }): SelectItem => ({
          label: state.nome,
          value: state.sigla,
        }),
      ),
    required: true,
  },
  {
    name: 'city',
    label: 'VERIFY_BUSINESS_CITY',
    type: FieldType.text,
    columnSpan: 1,
    required: true,
  },
  {
    name: 'zipCode',
    label: 'VERIFY_BUSINESS_ZIP',
    type: FieldType.numeric,
    precision: 0,
    columnSpan: 1,
    required: true,
  },
];

const countriesResolver = {
  resolve: async (): Promise<readonly SelectItem[]> => {
    const response = await fetch('/countries/index.json');
    return await response.json();
  },
  dependencies: [],
};

// FIXME: use the actual type parameter
const beneficialOwners: ChildStep<any> = {
  type: kyb.Steps.beneficialOwners,
  accessor: ['organization'],
  path: 'beneficial-owners',
  title: 'VERIFY_BUSINESS_BENEFICIAL_OWNERS_TITLE',
  description: 'VERIFY_BUSINESS_BENEFICIAL_OWNERS_DESC',
  fields: [
    {
      name: 'owners',
      title: 'VERIFY_BUSINESS_OWNER',
      itemTitle: 'VERIFY_BUSINESS_BENEFICIAL_OWNER_COUNTER',
      label: 'VERIFY_BUSINESS_ADD_ANOTHER_OWNER',
      type: FieldType.list,
      fields: [
        {
          name: 'owner',
          label: 'VERIFY_BUSINESS_OWNER',
          type: FieldType.embedded,
          columnsCount: 2,
          fields: [
            {
              name: 'firstName',
              label: 'VERIFY_BUSINESS_FIRST_NAME',
              type: FieldType.text,
              columnSpan: 1,
              required: true,
            },
            {
              name: 'lastName',
              label: 'VERIFY_BUSINESS_LAST_NAME',
              type: FieldType.text,
              columnSpan: 1,
              required: true,
            },
            {
              name: 'email',
              label: 'VERIFY_BUSINESS_EMAIL',
              type: FieldType.text,
              columnSpan: 1,
              required: true,
            },
            {
              name: 'phoneNumber',
              label: 'VERIFY_BUSINESS_PHONE',
              type: FieldType.phoneNumber,
              columnSpan: 1,
              displayErrors: true,
              required: true,
            },
            {
              name: 'taxId',
              label: 'VERIFY_BUSINESS_TAX_ID',
              type: FieldType.cnpj,
              validation: {
                type: 'length',
                description: 'CNPJ must follow this format: 12.345.678/9012-34',
                min: 14,
                max: 14,
              },
              columnSpan: 2,
              required: true,
            },
            {
              name: 'share',
              title: '',
              label: 'VERIFY_BUSINESS_PERCENTAGE_OWNERSHIP',
              type: FieldType.numeric,
              precision: 0,
              columnSpan: 2,
              required: true,
            },
            {
              name: 'nationality',
              title: '',
              label: 'VERIFY_BUSINESS_NATIONALITY',
              type: FieldType.select,
              source: countriesResolver,
              columnSpan: 2,
              required: true,
            },
          ],
          required: true,
        },
        {
          name: '',
          title: 'VERIFY_BUSINESS_SOURCE_OF_FUNDS',
          label: 'VERIFY_BUSINESS_SOURCE_OF_FUNDS',
          type: FieldType.embedded,
          columnsCount: 1,
          fields: [
            {
              name: 'sourceOfWealth',
              label: 'VERIFY_BUSINESS_SOURCE_OF_WEALTH',
              type: FieldType.select,
              source: javaEnumToSelectItem(sourceOfWealthTypes),
              columnSpan: 1,
              required: true,
            },
            {
              name: 'occupation',
              label: 'VERIFY_BUSINESS_OCCUPATION',
              type: FieldType.select,
              source: javaEnumToSelectItem(occupationTypes),
              columnSpan: 1,
              required: true,
            },
            {
              name: 'annualIncome',
              label: 'VERIFY_BUSINESS_ANNUAL_INCOME',
              type: FieldType.numeric,
              precision: 2,
              columnSpan: 1,
              required: true,
            },
          ],
          required: true,
        },
        {
          name: 'address',
          title: 'VERIFY_BUSINESS_BENEFICIAL_OWNER_ADDRESS_QUESTION',
          label: 'VERIFY_BUSINESS_ADDRESS_LABEL',
          type: FieldType.composite,
          columnsCount: 3,
          fields: createAddressFields({
            resolve: async (nationality?: string): Promise<readonly SelectItem[]> => {
              if (!nationality) {
                return [];
              }
              const country = nationality.toLowerCase();

              // Dynamically import states
              const { states } = await import(`./countries/${country}/index.json`);

              return states.map(
                (state: { readonly name: string; readonly id: string }): SelectItem => ({
                  label: state.name,
                  value: [country, state.id].join('/'),
                }),
              );
            },
            dependencies: ['nationality'],
          }),
          required: true,
        },
        {
          name: 'document',
          title: '',
          label: 'VERIFY_BUSINESS_UPLOAD_ID',
          type: FieldType.file,
          path: 'owners/{id}/files',
          required: true,
        },
      ],
      required: true,
    },
  ],
};

// FIXME: use the actual type parameter
const legalRepresentative: ChildStep<any> = {
  type: kyb.Steps.legalRepresentative,
  accessor: ['organization', 'legalRepresentative'],
  path: 'legal-representative',
  title: 'VERIFY_BUSINESS_LEGAL_REPRESENTATIVE_TITLE',
  skip: getSoleProprietorSkipMessage,
  description: 'VERIFY_BUSINESS_LEGAL_REPRESENTATIVE_DESC',
  fields: [
    {
      name: 'legalRepresentative',
      title: 'VERIFY_BUSINESS_LEGAL_REPRESENTATIVE_QUESTION',
      label: 'VERIFY_BUSINESS_LEGAL_REPRESENTATIVE_LABEL',
      type: FieldType.embedded,
      columnsCount: 2,
      fields: [
        {
          name: 'firstName',
          label: 'VERIFY_BUSINESS_FIRST_NAME',
          type: FieldType.text,
          columnSpan: 1,
          required: true,
        },
        {
          name: 'lastName',
          label: 'VERIFY_BUSINESS_LAST_NAME',
          type: FieldType.text,
          columnSpan: 1,
          required: true,
        },
      ],
      required: true,
    },
    {
      name: 'email',
      title: 'VERIFY_BUSINESS_EMAIL_QUESTION',
      label: 'VERIFY_BUSINESS_EMAIL',
      type: FieldType.text,
      required: true,
    },
    {
      name: 'phoneNumber',
      title: 'VERIFY_BUSINESS_PHONE_QUESTION',
      label: 'VERIFY_BUSINESS_PHONE_NUMBER',
      type: FieldType.phoneNumber,
      displayErrors: true,
      required: true,
    },
    {
      name: 'document',
      title: 'VERIFY_BUSINESS_SUPPORTED_ID_DOCS',
      label: 'VERIFY_BUSINESS_SUPPORTED_ID_DOCS',
      type: FieldType.file,
      path: 'legalRepresentative/files',
      required: true,
    },
  ],
};

// FIXME: use the actual type parameter
const businessContact: ChildStep<any> = {
  type: kyb.Steps.businessContact,
  accessor: ['organization', 'contact'],
  path: 'business-contact',
  title: 'VERIFY_BUSINESS_BUSINESS_CONTACT_TITLE',
  description: 'VERIFY_BUSINESS_BUSINESS_CONTACT_DESC',
  fields: [
    {
      name: 'pointOfContact',
      title: 'VERIFY_BUSINESS_CONTACT_QUESTION',
      label: 'VERIFY_BUSINESS_CONTACT_LABEL',
      type: FieldType.embedded,
      columnsCount: 2,
      fields: [
        {
          name: 'firstName',
          label: 'VERIFY_BUSINESS_FIRST_NAME',
          type: FieldType.text,
          columnSpan: 1,
          required: true,
        },
        {
          name: 'lastName',
          label: 'VERIFY_BUSINESS_LAST_NAME',
          type: FieldType.text,
          columnSpan: 1,
          required: true,
        },
      ],
      required: true,
    },
    {
      name: 'contactInformation',
      label: 'VERIFY_BUSINESS_CONTACT_INFORMATION',
      title: 'VERIFY_BUSINESS_CONTACT_INFORMATION_QUESTION',
      type: FieldType.embedded,
      columnsCount: 2,
      fields: [
        {
          name: 'email',
          title: '',
          label: 'VERIFY_BUSINESS_EMAIL',
          type: FieldType.email,
          required: true,
        },
        {
          name: 'phoneNumber',
          title: '',
          label: 'VERIFY_BUSINESS_PHONE_NUMBER',
          type: FieldType.phoneNumber,
          displayErrors: true,
          required: true,
        },
      ],
      required: true,
    },
    {
      name: 'position',
      title: 'VERIFY_BUSINESS_POSITION_QUESTION',
      label: 'VERIFY_BUSINESS_POSITION',
      type: FieldType.text,
      required: true,
    },
  ],
};

enum USDRange {
  zeroToTwoHundredFiftyThousand = '0-250000',
  twoHundredFiftyThousandToFiveHundredThousand = '250000-500000',
  fiveHundredThousandToOneMillion = '500000-1000000',
  oneMillionToFiveMillion = '1000000-5000000',
  fiveMillionToTenMillion = '5000000-10000000',
  overTenMillion = '10000000+',
}

// FIXME: use the actual type parameter
const sourceOfFunds: ChildStep<any> = {
  type: kyb.Steps.sourceOfFunds,
  accessor: ['operations', 'fundsSource'],
  path: 'source-of-funds',
  title: 'VERIFY_BUSINESS_SOURCE_OF_FUNDS_TITLE',
  description: 'VERIFY_BUSINESS_SOURCE_OF_FUNDS_DESC',
  fields: [
    {
      name: 'thirdPartyBeneficiaries',
      title: 'VERIFY_BUSINESS_THIRD_PARTY_QUESTION',
      type: FieldType.boolean,
      required: true,
    },
    {
      name: 'usdVolume',
      title: 'VERIFY_BUSINESS_USD_VOLUME_QUESTION',
      label: 'VERIFY_BUSINESS_EXPECTED_RANGE',
      type: FieldType.select,
      source: [
        {
          value: USDRange.zeroToTwoHundredFiftyThousand,
          label: '$0 - $250,000',
        },
        {
          value: USDRange.twoHundredFiftyThousandToFiveHundredThousand,
          label: '$250,000 - $500,000',
        },
        {
          value: USDRange.fiveHundredThousandToOneMillion,
          label: '$500,000 - $1 million',
        },
        {
          value: USDRange.oneMillionToFiveMillion,
          label: '$1 million - $5 million',
        },
        {
          value: USDRange.fiveMillionToTenMillion,
          label: '$5 million - $10 million',
        },
        {
          value: USDRange.overTenMillion,
          label: 'Over $10 million',
        },
      ],
      required: true,
    },
  ],
};

// FIXME: use the actual type parameter
const yourCustomers: ChildStep<any> = {
  type: kyb.Steps.yourCustomers,
  accessor: ['operations', 'customers'],
  path: 'your-customers',
  title: 'VERIFY_BUSINESS_YOUR_CUSTOMERS_TITLE',
  description: 'VERIFY_BUSINESS_YOUR_CUSTOMERS_DESC',
  fields: [
    {
      name: 'customerType',
      title: 'VERIFY_BUSINESS_CUSTOMER_TYPE_QUESTION',
      type: FieldType.multipleValues,
      items: javaEnumToSelectItem(beneficiaryTypes),
      required: true,
    },
    {
      name: 'typicalCustomer',
      title: 'VERIFY_BUSINESS_TYPICAL_CUSTOMER_QUESTION',
      label: 'VERIFY_BUSINESS_CUSTOMER_DESCRIPTION',
      description: [
        {
          type: 'p',
          content: makeParagraph(
            'Please share a detailed description of your typical customer offer.',
            'A complete response will allow us to approve your application faster.',
          ),
        },
        {
          type: 'list',
          title: 'Sample responses',
          items: [
            'Our customers are large retail chains including ABC and XYZ.',
            'We serve medium-sized online merchants from Asia selling beauty products in Brazil',
          ],
        },
      ],
      type: FieldType.paragraph,
      required: true,
    },
    {
      name: 'countries',
      title: 'VERIFY_BUSINESS_CUSTOMER_COUNTRIES_QUESTION',
      label: 'VERIFY_BUSINESS_COUNTRIES',
      multiple: true,
      type: FieldType.select,
      source: countriesResolver,
      required: true,
    },
    {
      name: 'quantity',
      title: 'VERIFY_BUSINESS_CUSTOMER_QUANTITY_QUESTION',
      label: 'VERIFY_BUSINESS_NUMBER_OF_CUSTOMERS',
      type: FieldType.numeric,
      precision: 0,
      required: true,
    },
    {
      name: 'industries',
      hidden: (data: Customer): boolean => !hasBusinessCustomers(data),
      title: 'VERIFY_BUSINESS_CUSTOMER_INDUSTRIES_QUESTION',
      type: FieldType.multipleValues,
      items: javaEnumToSelectItem(customerIndustryTypes),
      required: true,
    },
  ],
};

// FIXME: use the actual type parameter
const companyProfile: ChildStep<any> = {
  type: kyb.Steps.companyProfile,
  accessor: ['operations', 'profile'],
  path: 'company-profile',
  title: 'VERIFY_BUSINESS_COMPANY_PROFILE_TITLE',
  description: 'VERIFY_BUSINESS_COMPANY_PROFILE_DESC',
  fields: [
    {
      name: 'industry',
      title: 'VERIFY_BUSINESS_INDUSTRY_QUESTION',
      label: 'VERIFY_BUSINESS_MAIN_INDUSTRY',
      type: FieldType.select,
      source: javaEnumToSelectItem(industryTypes),
      required: true,
    },
    {
      name: 'services',
      title: 'VERIFY_BUSINESS_SERVICES_QUESTION',
      label: 'VERIFY_BUSINESS_SERVICES_DESCRIPTION',
      description: [
        {
          type: 'p',
          content: makeParagraph(
            'Please share a detailed description of the products or services you offer.',
            'A complete response will allow us to approve your application faster.',
          ),
        },
        {
          type: 'list',
          title: 'Sample responses',
          items: [
            'We provide accounting and tax advisory services for small and medium businesses in Brazil.',
            'We are a wholesale distributor and exporter of automotive parts for large retail chains.',
          ],
        },
      ],
      type: FieldType.paragraph,
      required: true,
    },
    {
      name: 'countries',
      title: 'VERIFY_BUSINESS_OPERATION_COUNTRIES_QUESTION',
      label: 'VERIFY_BUSINESS_COUNTRIES',
      type: FieldType.select,
      source: countriesResolver,
      multiple: true,
      required: true,
    },
    {
      name: 'isFinancialInstitution',
      title: 'VERIFY_BUSINESS_FINANCIAL_INSTITUTION_QUESTION',
      type: FieldType.boolean,
      required: true,
    },
    {
      name: 'hasBrazilLicenseRequirement',
      title: 'VERIFY_BUSINESS_LICENSE_REQUIRED',
      type: FieldType.boolean,
      required: true,
    },
  ],
};

// FIXME: use the actual type parameter
const onlinePresence: ChildStep<any> = {
  type: kyb.Steps.onlinePresence,
  accessor: ['socialNetwork'],
  path: 'online-presence',
  title: 'VERIFY_BUSINESS_ONLINE_PRESENCE_TITLE',
  description: 'VERIFY_BUSINESS_ONLINE_PRESENCE_DESC',
  fields: [
    {
      name: 'website',
      title: 'VERIFY_BUSINESS_WEBSITE_QUESTION',
      label: 'VERIFY_BUSINESS_WEBSITE_LINK',
      type: FieldType.text,
      required: true,
    },
    {
      name: 'socialMediaNetworks',
      title: 'VERIFY_BUSINESS_SOCIAL_MEDIA_QUESTION',
      label: 'VERIFY_BUSINESS_ADD_SOCIAL_MEDIA',
      type: FieldType.list,
      fields: [
        {
          name: 'handle',
          type: FieldType.text,
          label: 'VERIFY_BUSINESS_URL',
          required: true,
        },
      ],
      required: false,
    },
  ],
};

// FIXME: use the actual type parameter
const companyDetails: ChildStep<CustomerCompanyDetailsPatchRequest> = {
  type: kyb.Steps.companyDetails,
  accessor: [],
  path: 'company-details',
  title: 'VERIFY_BUSINESS_COMPANY_DETAILS_TITLE',
  description: '',
  fields: [
    {
      name: 'commercialName',
      title: 'VERIFY_BUSINESS_COMMERCIAL_NAME_QUESTION',
      label: 'VERIFY_BUSINESS_COMMERCIAL_NAME',
      type: FieldType.text,
      required: true,
    },
    {
      name: 'legalEntityName',
      title: 'VERIFY_BUSINESS_LEGAL_ENTITY_NAME_QUESTION',
      label: 'VERIFY_BUSINESS_LEGAL_ENTITY_NAME',
      type: FieldType.text,
      help: 'VERIFY_BUSINESS_LEGAL_ENTITY_NAME_HELP',
      required: true,
    },
    {
      name: 'entityType',
      title: 'VERIFY_BUSINESS_ENTITY_TYPE_QUESTION',
      label: 'VERIFY_BUSINESS_ENTITY_TYPE',
      type: FieldType.select,
      source: javaEnumToSelectItem(entityTypes),
      required: true,
    },
    {
      name: 'taxId',
      title: 'VERIFY_BUSINESS_CNPJ_QUESTION',
      label: 'VERIFY_BUSINESS_CNPJ',
      type: FieldType.cnpj,
      validation: {
        type: 'length',
        description: 'VERIFY_BUSINESS_CNPJ_FORMAT',
        min: 14,
        max: 14,
      },
      displayErrors: true,
      // FIXME: it must be a required field, but the problem with it is
      //        that it displays the error because we are evaluating the
      //        form "Too often"
      required: false,
    },
    {
      name: 'address',
      label: 'VERIFY_BUSINESS_ADDRESS_LABEL',
      title: 'VERIFY_BUSINESS_MAIN_ADDRESS_QUESTION',
      type: FieldType.composite,
      columnsCount: 3,
      fields: createAddressFields(),
      required: true,
    },
    {
      name: 'operationStartDate',
      title: 'VERIFY_BUSINESS_OPERATION_START_QUESTION',
      label: 'VERIFY_BUSINESS_OPERATION_STARTED',
      type: FieldType.date,
      required: true,
    },
  ],
};

const steps: readonly ParentStep<any>[] = [
  {
    type: kyb.Steps.businessInformation,
    path: 'business-information',
    title: 'VERIFY_BUSINESS_TITLE',
    children: [companyDetails, onlinePresence],
  },
  {
    type: kyb.Steps.businessOperations,
    path: 'business-operations',
    title: 'VERIFY_BUSINESS_OPERATIONS_TITLE',
    children: [companyProfile, yourCustomers, sourceOfFunds],
  },
  {
    type: kyb.Steps.organization,
    path: 'organization',
    title: 'VERIFY_BUSINESS_ORGANIZATION_TITLE',
    children: [businessContact, legalRepresentative, beneficialOwners],
  },
  {
    type: kyb.Steps.documents,
    path: 'documents',
    title: 'VERIFY_BUSINESS_DOCUMENTS_TITLE',
    children: [
      {
        type: kyb.Steps.documents,
        accessor: ['documents'],
        path: 'documents',
        title: 'VERIFY_BUSINESS_DOCUMENTS',
        description: 'VERIFY_BUSINESS_DOCUMENTS_DESCRIPTION',
        fields: [
          {
            name: 'incorporation',
            title: 'VERIFY_BUSINESS_INCORPORATION_DOCUMENT',
            label: 'VERIFY_BUSINESS_INCORPORATION_DOCUMENT',
            type: FieldType.file,
            path: 'documents/incorporation',
            required: true,
          },
          {
            name: 'cnpjRegistration',
            title: 'VERIFY_BUSINESS_CNPJ_REGISTRATION',
            label: 'VERIFY_BUSINESS_CNPJ_REGISTRATION',
            type: FieldType.file,
            path: 'documents/cnpjRegistration',
            required: true,
          },
          {
            name: 'powerOfAttorney',
            title: 'VERIFY_BUSINESS_POWER_OF_ATTORNEY',
            label: 'VERIFY_BUSINESS_POWER_OF_ATTORNEY',
            type: FieldType.file,
            help: 'VERIFY_BUSINESS_POWER_OF_ATTORNEY_HELP',
            path: 'documents/powerOfAttorney',
            required: true,
          },
          {
            name: 'bankAccountStatement',
            title: 'VERIFY_BUSINESS_BANK_STATEMENT',
            label: 'VERIFY_BUSINESS_BANK_STATEMENT',
            type: FieldType.file,
            path: 'documents/bankAccountStatement',
            required: true,
          },
        ],
      },
    ],
  },
];

export default steps;
